import { CodeEditor } from "../code/CodeEditor";

export const CodeEditorHook = {
  async mounted() {
    const $editor = this.el.querySelector("div");
    const initialContent = $editor.dataset.content || "";
    const language = $editor.dataset.language || "lua";
    this.requestedTheme = $editor.dataset.theme || "dark";
    const disabled = $editor.dataset.disabled === "true";
    const onChange = $editor.dataset.onChange || "code_updated";
    const saveKey = $editor.dataset.saveKey;

    this.$editor = $editor;
    this.editor = new CodeEditor($editor, {
      initialContent,
      language,
      theme: this.resolveTheme(),
      disabled,
      onChange: (content) => {
        this.pushEvent(onChange, { content });
        this.saveToLocalStorage(saveKey, content);
      },
    });

    // With theme="auto" the editor follows the app theme. Because the
    // component is phx-update="ignore", LiveView never re-runs updated() to
    // re-read the theme, so observe the <html> data-theme attribute directly
    // (covers ThemeToggle, SessionDarkMode, and the pre-paint inline script)
    // and reconfigure the editor when it changes.
    if (this.requestedTheme === "auto") {
      this.themeObserver = new MutationObserver(() => {
        this.editor.setTheme(this.resolveTheme());
      });
      this.themeObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["data-theme"],
      });
    }

    await this.initialize(saveKey);
  },

  resolveTheme() {
    if (this.requestedTheme !== "auto") return this.requestedTheme;
    return document.documentElement.getAttribute("data-theme") === "dark" ? "dark" : "light";
  },

  updated() {
    // TODO: Currently this Hook does not support updating the content of the
    //       code editor by updating the props (the prop only acts as initial
    //       content). Because of phx-update="ignore" we won't receive an update
    //       here. For now the Svelte version supports prop updating.

    const content = this.$editor.dataset.content || "";
    this.editor.setContent(content);
  },

  destroyed() {
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
    if (this.editor) {
      this.editor.destroy();
    }
  },

  async initialize(saveKey) {
    await this.editor.initialize();
    this.loadFromLocalStorage(saveKey);
  },

  saveToLocalStorage(key, value) {
    if (key) {
      window.dispatchEvent(
        new CustomEvent("local_storage_save", {
          detail: { key, value },
        }),
      );
    }
  },

  loadFromLocalStorage(key) {
    if (key) {
      window.addEventListener(
        "code_editor_snippet_loaded",
        (event) => {
          const { content } = event.detail;
          this.editor.setContent(content);
        },
        { once: true },
      );

      window.dispatchEvent(
        new CustomEvent("local_storage_load", {
          detail: { key, event_name: "code_editor_snippet_loaded" },
        }),
      );
    }
  },
};
